import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { PageCta } from "@/components/shared/PageCta";
import { Section } from "@/components/ui-kit/Kit";
import { Reveal } from "@/components/motion/Reveal";

const Item = ({ q, a, open, onToggle, testid }) => (
  <div className="border-b border-navy/10">
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      aria-expanded={open}
      className="flex w-full items-start justify-between gap-6 py-6 text-left"
    >
      <span className={`font-display text-base font-medium sm:text-lg ${open ? "text-grn" : "text-navy"}`}>{q}</span>
      <Plus
        className={`mt-1 h-4 w-4 shrink-0 transition-transform duration-400 ${open ? "rotate-45 text-grn" : "text-navy/40"}`}
      />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="max-w-3xl pb-7 text-sm leading-relaxed text-slate-600">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Faq() {
  const { t } = useI18n();
  const p = t("pages.faq");
  const [open, setOpen] = useState("0-0");

  const allQs = (p.groups || []).flatMap((g) => g.items);
  useSEO({
    title: p.title,
    description: p.lead,
    path: "/faq",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allQs.map((i) => ({
        "@type": "Question",
        name: i.q,
        acceptedAnswer: { "@type": "Answer", text: i.a },
      })),
    },
  });

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="faq-page">
        <div className="space-y-16">
          {(p.groups || []).map((g, gi) => (
            <Reveal key={g.title}>
              <div className="grid gap-8 lg:grid-cols-[240px_1fr] lg:gap-16">
                <div className="lg:sticky lg:top-28 lg:self-start">
                  <span className="font-mono text-[11px] tracking-[0.24em] text-grn">
                    {String(gi + 1).padStart(2, "0")}
                  </span>
                  <h2 className="mt-3 font-display text-2xl font-semibold text-navy">{g.title}</h2>
                </div>
                <div className="border-t border-navy/10">
                  {g.items.map((it, ii) => {
                    const key = `${gi}-${ii}`;
                    return (
                      <Item
                        key={it.q}
                        q={it.q}
                        a={it.a}
                        open={open === key}
                        onToggle={() => setOpen(open === key ? null : key)}
                        testid={`faq-item-${gi}-${ii}`}
                      />
                    );
                  })}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>
      <PageCta />
    </>
  );
}
